import React, { useState, useRef, useEffect, useMemo } from 'react';

/*
 * Country dialling-code picker with real flags and type-to-filter.
 *
 * Not a native <select>, for two reasons:
 *
 *  1. Flags. An <option> can only contain text, so the only way to show a flag
 *     in a native select is the emoji — and Windows has no flag glyphs at all
 *     (Segoe UI Emoji omits regional indicators), so Chrome, Edge and Firefox
 *     render "IN" instead of a flag for roughly half of all visitors.
 *
 *  2. Length. There are ~240 dialling codes. Scrolling that in a native
 *     dropdown is miserable; typing to filter is how people actually expect
 *     to find their country.
 *
 * Flags are self-hosted from public/flags, populated at build time by
 * scripts/copy-flags.js. Deliberately not a CDN: this sits on the signup
 * page, and a site that advertises "Encrypted & private" shouldn't hand a
 * visitor's IP to a third party to draw a 20px picture.
 *
 * They still degrade in two steps — emoji, then the ISO code — so a missing
 * file costs cosmetics, never usability.
 */

const flagSrc = (iso2) => `/flags/${iso2.toLowerCase()}.svg`;

function Flag({ iso2, emoji, size = 20 }) {
  const [broken, setBroken] = useState(false);

  if (!iso2 || broken) {
    // Emoji where it renders, the ISO code where it doesn't. Either way the
    // row keeps its shape so the list doesn't jump.
    return (
      <span
        aria-hidden="true"
        style={{
          width: size, flexShrink: 0, textAlign: 'center',
          fontSize: '11px', fontWeight: 700, color: 'var(--gray-400)',
          letterSpacing: '-0.02em',
        }}
      >
        {emoji || (iso2 || '').toUpperCase()}
      </span>
    );
  }

  return (
    <img
      // SVG, so one file covers every density — no srcSet needed.
      src={flagSrc(iso2)}
      width={size}
      height={Math.round((size * 3) / 4)}
      alt=""
      aria-hidden="true"
      loading="lazy"
      onError={() => setBroken(true)}
      style={{
        flexShrink: 0, borderRadius: '2px', objectFit: 'cover',
        // Flags are mostly flat colour and several are white-on-white
        // (Japan, Poland); without this they dissolve into the menu.
        boxShadow: '0 0 0 1px rgba(0,0,0,0.10)',
        display: 'block',
      }}
    />
  );
}

export default function CountryCodeSelect({
  countries,
  value,            // the selected dialling code, e.g. "91"
  selectedIso2,     // disambiguates shared codes (+1 covers 25 countries)
  onSelect,         // (country) => void
  disabled,
  borderColor,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const wrapRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => countries.find(c => c.iso2 === selectedIso2)
      || countries.find(c => c.phone_code === value),
    [countries, selectedIso2, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;

    // Name, ISO code, or the dialling code with or without its plus — people
    // reach for all three.
    const bare = q.replace(/^\+/, '');
    return countries.filter(c =>
      c.name.toLowerCase().includes(q)
      || c.iso2.toLowerCase() === q
      || c.phone_code.startsWith(bare),
    );
  }, [countries, query]);

  // Close on an outside click or Escape.
  useEffect(() => {
    if (!open) return undefined;

    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);

    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(Math.max(0, countries.findIndex(c => c.iso2 === selected?.iso2)));
      // Focus the field, not the list: typing is the primary way in.
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open, countries, selected]);

  // Keep the highlighted row visible while arrowing through 240 options.
  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const choose = (country) => {
    onSelect(country);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => {
        const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
        return Math.max(0, Math.min(filtered.length - 1, next));
      });
      return;
    }

    if (e.key === 'Enter' && filtered[activeIndex]) {
      e.preventDefault();
      choose(filtered[activeIndex]);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={selected ? `Country code: ${selected.name} +${selected.phone_code}` : 'Select country code'}
        data-managed-hover=""
        style={{
          display: 'flex', alignItems: 'center', gap: '7px',
          // Only the flag and the code live here; the country name is in the
          // list. That is narrower than the old select AND more legible,
          // because nothing is truncated.
          height: '43px', padding: '0 10px',
          border: `1.5px solid ${borderColor}`, borderRadius: '8px',
          background: 'var(--white)',
          fontSize: '14px', color: 'var(--gray-900)', fontWeight: 500,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'border-color 0.15s',
        }}
      >
        <Flag iso2={selected?.iso2} emoji={selected?.emoji} />
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          +{selected?.phone_code || '—'}
        </span>
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', opacity: 0.5 }}>
          <path d="M1 3.5 5 7.5 9 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 40,
            width: '290px', maxWidth: 'calc(100vw - 48px)',
            background: 'var(--white)',
            border: '1px solid var(--gray-200)', borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow-lg)', overflow: 'hidden',
          }}
        >
          <div style={{ padding: '8px', borderBottom: '1px solid var(--gray-100)' }}>
            <input
              ref={searchRef}
              value={query}
              onChange={e => { setQuery(e.target.value); setActiveIndex(0); }}
              onKeyDown={onKeyDown}
              placeholder="Search country or code"
              aria-label="Search countries"
              style={{
                width: '100%', padding: '8px 10px', fontSize: '13.5px',
                border: '1.5px solid var(--gray-200)', borderRadius: '7px',
                outline: 'none', color: 'var(--gray-900)',
              }}
            />
          </div>

          <div ref={listRef} role="listbox" style={{ maxHeight: '260px', overflowY: 'auto', padding: '4px' }}>
            {filtered.length === 0 && (
              <p style={{ padding: '18px 12px', textAlign: 'center', fontSize: '13px', color: 'var(--gray-500)' }}>
                No country matches “{query}”.
              </p>
            )}

            {filtered.map((c, i) => {
              const isSelected = c.iso2 === selected?.iso2;
              const isActive = i === activeIndex;

              return (
                <div
                  key={c.iso2}
                  role="option"
                  aria-selected={isSelected}
                  data-active={isActive}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => choose(c)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 10px', borderRadius: '7px', cursor: 'pointer',
                    background: isActive ? 'var(--red-50)' : 'transparent',
                  }}
                >
                  <Flag iso2={c.iso2} emoji={c.emoji} />
                  <span style={{
                    flex: 1, minWidth: 0, fontSize: '13.5px',
                    color: isSelected ? 'var(--red-dark)' : 'var(--gray-800)',
                    fontWeight: isSelected ? 600 : 400,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {c.name}
                  </span>
                  <span style={{
                    fontSize: '12.5px', color: 'var(--gray-500)',
                    fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                  }}>
                    +{c.phone_code}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
